'use client';

import CustomButton from '@/components/custom-button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ProcessingMethod } from '@/types/enum';
import { Search, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { memo, useMemo, useRef, useState } from 'react';

export type FilterProps = {
    processingMethod?: string;
    searchStr?: string;
};

type Props = {
    filter?: FilterProps;
    onFilter: (filter: FilterProps) => void;
};

export const ProcedureFilter = memo(({ filter, onFilter }: Props) => {
    const transDef = useTranslations('System');
    const transPage = useTranslations('ProcedureManagement');

    const inputRef = useRef<HTMLInputElement>(null);

    // processingMethod: HOÀN TOÀN derived từ `filter` (URL). Không dùng local state
    // để tránh: (1) lệch giá trị khi parent populate filter async sau init, (2) mount-fire
    // ghi đè URL về 'all'. Click button -> gọi onFilter THẲNG.
    const selectedProcessingApproach = filter?.processingMethod ?? 'all';

    // inputValue: local state cho ô tìm kiếm (debounce-ish qua Enter / nút Search).
    // Init từ filter để hiển thị search đã có trên URL khi mở lại trang.
    const [inputValue, setInputValue] = useState(() => filter?.searchStr ?? '');

    // Memo hóa danh sách hình thức xử lý: chỉ phụ thuộc bản dịch, tránh tạo
    // mảng/object mới mỗi lần render gây re-render các nút con không cần thiết.
    const processingMethodOptions = useMemo(
        () => [
            { label: transDef('System_All'), value: 'all' },
            {
                label: transDef('System_HandoverMethod_Offline'),
                value: `${ProcessingMethod.Offline}`,
            },
            {
                label: transDef('System_HandoverMethod_Online'),
                value: `${ProcessingMethod.Online}`,
            },
        ],
        [transDef]
    );

    const submitSearch = (nextSearch: string) => {
        onFilter({
            processingMethod: selectedProcessingApproach,
            searchStr: nextSearch,
        });
    };

    const handleProcessingMethodClick = (value: string) => {
        // Gọi onFilter trực tiếp -> parent ghi URL. Không dùng setState trung gian
        // để tránh sync-effect overwrite khi state lệch URL ở mount đầu.
        onFilter({
            processingMethod: value,
            searchStr: inputValue,
        });
    };

    const handleClear = () => {
        setInputValue('');
        if (inputRef.current) inputRef.current.value = '';
        submitSearch('');
    };

    return (
        <div className='flex flex-wrap items-stretch gap-2 rounded-xl border border-border bg-white p-3 shadow-sm lg:gap-4 lg:p-4'>
            {/* Processing method toggle */}
            <div className='flex flex-col items-start gap-1.5'>
                <label className='hidden text-sm font-medium tracking-wide lg:block'>
                    {transPage('ProcedureManagement_Formality')}
                </label>
                <div className='flex gap-1 rounded-lg bg-muted/60 p-1'>
                    {processingMethodOptions.map((option) => (
                        <button
                            key={option.value}
                            onClick={() =>
                                handleProcessingMethodClick(option.value)
                            }
                            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-150 lg:px-3.5 lg:text-[13px] ${
                                selectedProcessingApproach === option.value
                                    ? cn('bg-primary text-white shadow-sm', {
                                          'bg-blue-600':
                                              option.value ===
                                              ProcessingMethod.Online.toString(),
                                          'bg-amber-600':
                                              option.value ===
                                              ProcessingMethod.Offline.toString(),
                                      })
                                    : 'text-muted-foreground hover:bg-background hover:text-foreground'
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Search */}
            <div className='flex flex-1 items-stretch gap-2 max-md:w-full lg:gap-4'>
                <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                    <label className='hidden text-sm font-medium tracking-wide lg:block'>
                        {transDef('System_Search')}
                    </label>
                    <div className='relative flex items-center'>
                        <Search
                            size={14}
                            strokeWidth={2.5}
                            className='absolute left-3 text-muted-foreground'
                        />
                        <Input
                            ref={inputRef}
                            className='no-ring h-9 rounded-lg border-border bg-muted/40 pl-8 pr-8 text-[13px] placeholder:text-muted-foreground/60'
                            placeholder={`${transDef('System_SearchBy')} ${transPage('ProcedureManagement_ProcedureName').toLowerCase()} ...`}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    submitSearch(inputValue);
                                }
                            }}
                        />
                        {!!inputValue && (
                            <button
                                onClick={handleClear}
                                className='absolute right-2.5 rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-red-500'
                            >
                                <X size={13} strokeWidth={2.5} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Apply button — icon only on mobile, icon + text on desktop */}
                <div className='flex items-end'>
                    <CustomButton.Root
                        onClick={() => submitSearch(inputValue)}
                        className='h-9 shrink-0 rounded-lg bg-primary px-3 text-[13px] font-medium text-primary-foreground transition-opacity hover:bg-primary/90 hover:opacity-90 active:scale-[0.98] lg:px-4'
                        icon={<Search size={14} strokeWidth={2.5} />}
                    >
                        <span className='hidden lg:inline'>
                            {transDef('System_Search')}
                        </span>
                    </CustomButton.Root>
                </div>
            </div>
        </div>
    );
});

ProcedureFilter.displayName = 'ProcedureFilter';
